import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Plus, ChevronLeft, ChevronRight, Filter, X, AlertTriangle,
  Trash2, Pencil, Calendar as CalIcon, Clock,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatApiError } from "@/lib/errors";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function startOfWeek(d) { const dt = new Date(d); const day = (dt.getDay() + 6) % 7; dt.setDate(dt.getDate() - day); dt.setHours(0,0,0,0); return dt; }
function startOfMonth(d) { const dt = new Date(d.getFullYear(), d.getMonth(), 1); return dt; }
function endOfMonth(d)   { const dt = new Date(d.getFullYear(), d.getMonth()+1, 0, 23, 59, 59, 999); return dt; }
function addDays(d, n)   { const dt = new Date(d); dt.setDate(dt.getDate()+n); return dt; }
function sameDay(a, b)   { return a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate(); }
function fmtIsoLocal(dt) { return new Date(dt.getTime() - dt.getTimezoneOffset()*60000).toISOString().slice(0,16); }
function overlaps(entry, dayStart, dayEnd) {
  const s = new Date(entry.start); const e = new Date(entry.end);
  return s < dayEnd && e > dayStart;
}
function hexWithAlpha(hex, a=0.15) {
  const c = hex.replace("#",""); const n = parseInt(c.length===3 ? c.split("").map(x=>x+x).join("") : c, 16);
  return `rgba(${(n>>16)&255},${(n>>8)&255},${n&255},${a})`;
}

function buildMonthCells(date) {
  const first = startOfMonth(date);
  const firstCell = startOfWeek(first);
  const cells = [];
  for (let i = 0; i < 42; i++) cells.push(addDays(firstCell, i));
  return cells;
}

export default function Calendar() {
  const { user, hasPerm } = useAuth();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const canWrite = hasPerm("calendar.write");
  const canAdmin = hasPerm("calendar.admin");

  const [view, setView] = useState(sp.get("view") || "month"); // month|week|day|vehicles
  const [anchor, setAnchor] = useState(new Date());
  const [types, setTypes] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState([]);
  const [vehicleFilter, setVehicleFilter] = useState([]);
  const [clientFilter, setClientFilter] = useState([]); // [] all · ["none"] = entries w/o client · [clientId]
  const [windowDays, setWindowDays] = useState(14);
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [prefill, setPrefill] = useState(null);

  useEffect(() => {
    sp.set("view", view); setSp(sp, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // Window for fetching entries
  const fetchWindow = useMemo(() => {
    if (view === "month") {
      const first = startOfWeek(startOfMonth(anchor));
      return { start: first, end: addDays(first, 42) };
    }
    if (view === "week") {
      const s = startOfWeek(anchor);
      return { start: s, end: addDays(s, 7) };
    }
    if (view === "day") {
      const s = new Date(anchor); s.setHours(0,0,0,0);
      return { start: s, end: addDays(s, 1) };
    }
    const s = new Date(anchor); s.setHours(0,0,0,0);
    return { start: s, end: addDays(s, windowDays) };
  }, [view, anchor, windowDays]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({
        start: fetchWindow.start.toISOString(),
        end: fetchWindow.end.toISOString(),
      });
      const [t, v, e, uu, cc] = await Promise.all([
        api.get("/calendar/types"),
        api.get("/vehicles"),
        api.get(`/calendar/entries?${qs.toString()}`),
        canAdmin ? api.get("/admin/users").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        api.get("/clients").catch(() => ({ data: [] })),
      ]);
      setTypes(t.data); setVehicles(v.data); setUsers(uu.data); setClients(cc.data);
      setEntries(e.data);
    } catch (err) {
      toast.error(formatApiError(err) || "Failed to load calendar");
    } finally {
      setLoading(false);
    }
  }, [fetchWindow, canAdmin]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => entries.filter(e =>
    (typeFilter.length === 0 || typeFilter.includes(e.type_id)) &&
    (vehicleFilter.length === 0 || (e.resource_type === "vehicle" && vehicleFilter.includes(e.resource_id))) &&
    (clientFilter.length === 0 ||
      (clientFilter.includes("none") ? !e.client_id : false) ||
      (e.client_id && clientFilter.includes(e.client_id)))
  ), [entries, typeFilter, vehicleFilter, clientFilter]);

  const typeById = useMemo(() => Object.fromEntries(types.map(t => [t.id, t])), [types]);
  const vehicleById = useMemo(() => Object.fromEntries(vehicles.map(v => [v.id, v])), [vehicles]);
  const userById = useMemo(() => Object.fromEntries(users.map(u => [u.id, u])), [users]);
  const clientById = useMemo(() => Object.fromEntries(clients.map(c => [c.id, c])), [clients]);

  const colourFor = (e) => e.colour_override || typeById[e.type_id]?.colour || "#64748b";

  const headerLabel = useMemo(() => {
    if (view === "month") return anchor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
    if (view === "week") {
      const s = startOfWeek(anchor), e = addDays(s, 6);
      return `${s.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${e.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
    }
    if (view === "day") return anchor.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    return `${anchor.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${addDays(anchor, windowDays-1).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
  }, [view, anchor, windowDays]);

  const navDate = (delta) => {
    const d = new Date(anchor);
    if (view === "month") d.setMonth(d.getMonth() + delta);
    else if (view === "week") d.setDate(d.getDate() + 7 * delta);
    else if (view === "day") d.setDate(d.getDate() + delta);
    else d.setDate(d.getDate() + windowDays * delta);
    setAnchor(d);
  };

  const onOpenNew = (pf = null) => { setPrefill(pf); setEditing(null); setFormOpen(true); };
  const onEdit = (e) => { setEditing(e); setPrefill(null); setFormOpen(true); setDetail(null); };
  const onDelete = async (id) => {
    try { await api.delete(`/calendar/entries/${id}`); toast.success("Entry deleted"); setDetail(null); load(); }
    catch (err) { toast.error(formatApiError(err) || "Delete failed"); }
  };

  return (
    <div data-testid="calendar-page" className="flex h-[calc(100vh-4rem)] w-full flex-col bg-cargodash-subtle text-cargodash-ink">
      <div className="flex h-14 shrink-0 items-center gap-3 border-b border-slate-200 bg-white px-3 sm:px-5">
        <Button variant="ghost" size="sm" onClick={() => navigate("/")} data-testid="cal-back" className="gap-1.5 text-slate-600">
          <ArrowLeft className="h-4 w-4" /><span className="hidden sm:inline">Hub</span>
        </Button>
        <div className="mx-1 h-5 w-px bg-slate-200" />
        <div className="flex items-center gap-1">
          {["month","week","day","vehicles"].map(v => (
            <button key={v} type="button" onClick={() => setView(v)} data-testid={`cal-view-${v}`}
              className={`rounded-md px-2.5 py-1 text-xs font-medium capitalize transition ${view===v ? "bg-cargodash-primary-50 text-cargodash-primary-700" : "text-slate-600 hover:bg-slate-100"}`}>{v}</button>
          ))}
        </div>
        <div className="mx-2 h-5 w-px bg-slate-200" />
        <div className="flex items-center gap-1">
          <Button size="icon" variant="ghost" onClick={() => navDate(-1)} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button size="sm" variant="ghost" onClick={() => { setAnchor(new Date()); setSelectedDay(new Date()); }} data-testid="cal-today">Today</Button>
          <Button size="icon" variant="ghost" onClick={() => navDate(1)} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
        <div className="min-w-0 flex-1 truncate font-display text-sm font-semibold text-slate-900" data-testid="cal-header-label">{headerLabel}</div>
        <div className="hidden shrink-0 items-center gap-2 lg:flex">
          <Select value={typeFilter[0] || "all"} onValueChange={v => setTypeFilter(v === "all" ? [] : [v])}>
            <SelectTrigger className="h-8 w-[140px]" data-testid="cal-type-filter"><SelectValue placeholder="All types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {types.map(t => <SelectItem key={t.id} value={t.id}><span className="inline-block h-2 w-2 rounded-full mr-2 align-middle" style={{background:t.colour}} />{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={vehicleFilter[0] || "all"} onValueChange={v => setVehicleFilter(v === "all" ? [] : [v])}>
            <SelectTrigger className="h-8 w-[150px]" data-testid="cal-vehicle-filter"><SelectValue placeholder="All vehicles" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All vehicles</SelectItem>
              {vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.registration}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={clientFilter[0] || "all"} onValueChange={v => setClientFilter(v === "all" ? [] : [v])}>
            <SelectTrigger className="h-8 w-[150px]" data-testid="cal-client-filter"><SelectValue placeholder="All clients" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All clients</SelectItem>
              <SelectItem value="none">— No client —</SelectItem>
              {clients.filter(c => c.status === "active").map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {canWrite && (
          <Button
            size="sm"
            onClick={() => onOpenNew()}
            data-testid="cal-new-entry"
            className="shrink-0 gap-1.5 bg-cargodash-primary-600 hover:bg-cargodash-primary-700"
          >
            <Plus className="h-4 w-4" />New entry
          </Button>
        )}
      </div>

      <div className="flex-1 overflow-auto p-4 sm:p-6">
        {loading ? (
          <div className="py-20 text-center text-sm text-slate-500">Loading calendar…</div>
        ) : view === "month" ? (
          <MonthView date={anchor} entries={filtered} colourFor={colourFor} typeById={typeById} clientById={clientById}
                     selectedDay={selectedDay} setSelectedDay={setSelectedDay} onEntry={setDetail} onDayNew={d => onOpenNew({ start: d })} canWrite={canWrite} />
        ) : view === "week" ? (
          <WeekView date={anchor} entries={filtered} colourFor={colourFor} clientById={clientById} onEntry={setDetail} onDayNew={d => onOpenNew({ start: d })} canWrite={canWrite}
                    selectedDay={selectedDay} setSelectedDay={setSelectedDay} />
        ) : view === "day" ? (
          <DayView date={anchor} entries={filtered} colourFor={colourFor} typeById={typeById} vehicleById={vehicleById} clientById={clientById} onEntry={setDetail} canWrite={canWrite} onOpenNew={onOpenNew} />
        ) : (
          <VehiclesView anchor={anchor} windowDays={windowDays} setWindowDays={setWindowDays}
                        vehicles={[...vehicles]
                          .filter(v => v.status === "active")
                          .sort((a, b) => a.registration.localeCompare(b.registration, undefined, { numeric: true, sensitivity: "base" }))}
                        entries={filtered} colourFor={colourFor} clientById={clientById} onEntry={setDetail} onCellNew={(vehicle, day) => onOpenNew({ start: day, vehicle_id: vehicle.id })} canWrite={canWrite} typeById={typeById} />
        )}
      </div>

      {/* Entry form */}
      <EntryForm open={formOpen} onOpenChange={setFormOpen} editing={editing} prefill={prefill}
                 types={types} vehicles={vehicles} users={users} clients={clients}
                 onSaved={() => { setFormOpen(false); load(); }} />

      {/* Detail drawer */}
      <Sheet open={!!detail} onOpenChange={o => !o && setDetail(null)}>
        <SheetContent className="sm:max-w-md" data-testid="cal-detail-drawer">
          {detail && (() => {
            const t = typeById[detail.type_id];
            const canEditThis = canAdmin || user?.id === detail.created_by_user_id;
            const vehicle = detail.resource_type === "vehicle" ? vehicleById[detail.resource_id] : null;
            const assignee = detail.assignee_user_id ? userById[detail.assignee_user_id] : null;
            const client = detail.client_id ? clientById[detail.client_id] : null;
            return (
              <>
                <SheetHeader>
                  <div className="flex items-center gap-2">
                    <span className="h-3 w-3 rounded-full" style={{background: colourFor(detail)}} />
                    <SheetTitle data-testid="cal-detail-title">{detail.title}</SheetTitle>
                  </div>
                  <SheetDescription>
                    {new Date(detail.start).toLocaleString()} — {new Date(detail.end).toLocaleString()}
                  </SheetDescription>
                </SheetHeader>
                <div className="mt-4 space-y-3 text-sm">
                  <Row label="Type"><Badge style={{background: hexWithAlpha(t?.colour||"#64748b", 0.2), color: t?.colour}}>{t?.name}</Badge></Row>
                  {detail.client_id && (
                    <Row label="Client" data-testid="cal-detail-client">
                      {client ? (
                        <>
                          {client.name}
                          {client.status === "archived" && <span className="ml-1 text-slate-400">(archived)</span>}
                        </>
                      ) : (
                        <span className="text-slate-400">(archived)</span>
                      )}
                    </Row>
                  )}
                  {detail.resource_type === "vehicle" && (
                    <Row label="Vehicle">{vehicle ? `${vehicle.registration} — ${vehicle.make} ${vehicle.model}` : <span className="text-slate-400">(retired vehicle)</span>}</Row>
                  )}
                  {detail.resource_type === "user" && (
                    <Row label="User">{userById[detail.resource_id]?.email || detail.resource_id}</Row>
                  )}
                  {detail.all_day && <Row label="All-day">Yes</Row>}
                  {assignee && <Row label="Assignee">{assignee.full_name} ({assignee.email})</Row>}
                  {detail.description && <Row label="Notes"><span className="whitespace-pre-wrap">{detail.description}</span></Row>}
                  <Row label="Created by">{userById[detail.created_by_user_id]?.email || <span className="text-slate-400">deleted user</span>}</Row>
                </div>
                {canEditThis && (
                  <div className="mt-6 flex gap-2">
                    <Button variant="outline" onClick={() => onEdit(detail)} data-testid="cal-detail-edit" className="gap-1.5"><Pencil className="h-4 w-4" />Edit</Button>
                    <Button variant="outline" onClick={() => setConfirmDelete(detail)} data-testid="cal-detail-delete" className="gap-1.5 text-cargodash-alert-600 hover:bg-cargodash-alert-600/10">
                      <Trash2 className="h-4 w-4" />Delete
                    </Button>
                  </div>
                )}
              </>
            );
          })()}
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!confirmDelete} onOpenChange={o => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete "{confirmDelete?.title}"?</AlertDialogTitle>
          <AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { const id = confirmDelete.id; setConfirmDelete(null); onDelete(id); }} className="bg-cargodash-alert-600 hover:bg-cargodash-alert-600/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex gap-3">
      <div className="w-24 shrink-0 text-xs font-medium uppercase tracking-wider text-slate-500">{label}</div>
      <div className="min-w-0 flex-1 text-slate-700">{children}</div>
    </div>
  );
}

function MonthView({ date, entries, colourFor, typeById, clientById, selectedDay, setSelectedDay, onEntry, onDayNew, canWrite }) {
  const cells = buildMonthCells(date);
  const today = new Date();
  const entriesForDay = (d) => entries.filter(e => overlaps(e, new Date(d.getFullYear(), d.getMonth(), d.getDate()), new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23,59,59,999)));
  const dayEntries = entriesForDay(selectedDay);
  return (
    <div className="mx-auto max-w-7xl">
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="month-view">
        <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50 text-[11px] font-medium uppercase tracking-wider text-slate-500">
          {WEEKDAYS.map(w => <div key={w} className="px-2 py-2 text-center">{w}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((d) => {
            const inMonth = d.getMonth() === date.getMonth();
            const isToday = sameDay(d, today);
            const isSel = sameDay(d, selectedDay);
            const dayE = entriesForDay(d);
            const cellKey = d.toISOString().slice(0, 10);
            return (
              <div key={cellKey} onClick={() => setSelectedDay(d)} data-testid={`month-cell-${cellKey}`}
                   className={`min-h-[96px] cursor-pointer border-b border-r border-slate-100 p-1.5 text-[11px] transition ${inMonth ? "bg-white" : "bg-slate-50/60 text-slate-400"} ${isSel ? "ring-2 ring-inset ring-cargodash-primary-500" : ""}`}>
                <div className="mb-1 flex items-center justify-between">
                  <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] ${isToday ? "bg-cargodash-primary-600 font-semibold text-white" : "text-slate-600"}`}>{d.getDate()}</span>
                  {canWrite && inMonth && (
                    <button type="button" onClick={(ev) => { ev.stopPropagation(); onDayNew(d); }} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-slate-700">
                      <Plus className="h-3 w-3" />
                    </button>
                  )}
                </div>
                <div className="space-y-0.5">
                  {dayE.slice(0,3).map(e => (
                    <button key={e.id} type="button" onClick={(ev) => { ev.stopPropagation(); onEntry(e); }}
                            data-testid={`month-pill-${e.id}`}
                            className="block w-full truncate rounded px-1 py-0.5 text-left text-[10px] font-medium"
                            style={{background: hexWithAlpha(colourFor(e), 0.22), color: colourFor(e)}}>
                      {e.all_day ? "" : new Date(e.start).toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"})+" "} {e.title}
                    </button>
                  ))}
                  {dayE.length > 3 && <div className="px-1 text-[10px] text-slate-500">+{dayE.length-3} more</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4" data-testid="month-day-panel">
        <div className="mb-2 flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-900">{selectedDay.toLocaleDateString(undefined,{weekday:"long",month:"long",day:"numeric"})}</div>
          {canWrite && (
            <Button size="sm" variant="ghost" onClick={() => onDayNew(selectedDay)} data-testid="month-day-add"
                    className="gap-1 text-xs text-cargodash-primary-700 hover:bg-cargodash-primary-50">
              <Plus className="h-3.5 w-3.5" />Add entry on {selectedDay.toLocaleDateString(undefined,{month:"short",day:"numeric"})}
            </Button>
          )}
        </div>
        {dayEntries.length === 0 ? (
          <div className="text-sm text-slate-500">No entries.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {dayEntries.map(e => {
              const t = typeById[e.type_id];
              const cl = e.client_id ? clientById?.[e.client_id] : null;
              return (
                <li key={e.id}>
                  <button type="button" onClick={() => onEntry(e)} className="flex w-full items-start gap-3 py-2 text-left hover:bg-slate-50">
                    <span className="mt-1 h-2 w-2 rounded-full" style={{background: colourFor(e)}} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium text-slate-900">{e.title}</div>
                      <div className="text-xs text-slate-500">
                        {e.all_day ? "All day" : `${new Date(e.start).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})} – ${new Date(e.end).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}`}
                        {t && <> · <span style={{color: t.colour}}>{t.name}</span></>}
                      </div>
                      {e.client_id && (
                        <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600" data-testid={`day-list-client-${e.id}`}>
                          {cl ? cl.name : "(archived)"}
                          {cl && cl.status === "archived" && <span className="text-slate-400">(archived)</span>}
                        </div>
                      )}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function WeekView({ date, entries, colourFor, onEntry, onDayNew, canWrite, selectedDay, setSelectedDay }) {
  const s = startOfWeek(date);
  const days = Array.from({length:7}).map((_,i)=>addDays(s,i));
  const HOURS = Array.from({length:17},(_,i)=>i+6); // 06:00 – 22:00
  const today = new Date();
  return (
    <div className="mx-auto max-w-7xl overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="week-view">
      <div className="grid" style={{gridTemplateColumns:"56px repeat(7,1fr)"}}>
        <div className="border-b border-slate-200 bg-slate-50" />
        {days.map(d => (
          <div key={d.toISOString()} onClick={() => setSelectedDay(d)}
               className={`cursor-pointer border-b border-l border-slate-200 bg-slate-50 px-2 py-2 text-center text-[11px] ${sameDay(d,selectedDay) ? "bg-cargodash-primary-50" : ""}`}
               data-testid={`week-day-header-${d.toISOString().slice(0,10)}`}>
            <div className="text-slate-500">{d.toLocaleDateString(undefined,{weekday:"short"})}</div>
            <div className={`mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full text-[12px] ${sameDay(d,today)?"bg-cargodash-primary-600 text-white":"text-slate-800"}`}>{d.getDate()}</div>
          </div>
        ))}

        {/* All-day row */}
        <div className="border-b border-slate-100 bg-white px-1 py-1 text-[10px] text-slate-400 text-right pr-2">all-day</div>
        {days.map(d => {
          const ds = new Date(d.getFullYear(),d.getMonth(),d.getDate());
          const de = new Date(d.getFullYear(),d.getMonth(),d.getDate(),23,59,59,999);
          const ad = entries.filter(e => e.all_day && overlaps(e, ds, de));
          return (
            <div key={d.toISOString()+'ad'} className="min-h-[30px] border-b border-l border-slate-100 p-1">
              {ad.map(e => (
                <button key={e.id} onClick={() => onEntry(e)} className="block w-full truncate rounded px-1 py-0.5 text-left text-[10px] font-medium"
                        style={{background:hexWithAlpha(colourFor(e),0.22),color:colourFor(e)}}>{e.title}</button>
              ))}
            </div>
          );
        })}

        {/* Hour rows */}
        {HOURS.map(h => (
          <React.Fragment key={h}>
            <div className="border-b border-slate-100 px-1 py-1 text-right text-[10px] text-slate-400">{String(h).padStart(2,"0")}:00</div>
            {days.map(d => {
              const cellStart = new Date(d.getFullYear(),d.getMonth(),d.getDate(),h);
              const cellEnd = new Date(d.getFullYear(),d.getMonth(),d.getDate(),h+1);
              const within = entries.filter(e => !e.all_day && overlaps(e, cellStart, cellEnd));
              return (
                <div key={d.toISOString()+h} onClick={() => canWrite && onDayNew(cellStart)}
                     className="min-h-[32px] border-b border-l border-slate-100 p-0.5 hover:bg-slate-50">
                  {within.map(e => (
                    <button key={e.id} onClick={(ev) => { ev.stopPropagation(); onEntry(e); }}
                            className="block w-full truncate rounded px-1 py-0.5 text-left text-[10px] font-medium"
                            style={{background:hexWithAlpha(colourFor(e),0.22),color:colourFor(e)}}>
                      {new Date(e.start).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})} {e.title}
                    </button>
                  ))}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function DayView({ date, entries, colourFor, typeById, vehicleById, clientById, onEntry, canWrite, onOpenNew }) {
  const ds = new Date(date.getFullYear(),date.getMonth(),date.getDate());
  const de = new Date(date.getFullYear(),date.getMonth(),date.getDate(),23,59,59,999);
  const dayE = entries.filter(e => overlaps(e, ds, de));
  const byType = {};
  for (const e of dayE) { (byType[e.type_id] = byType[e.type_id] || []).push(e); }
  const HOURS = Array.from({length:17},(_,i)=>i+6);
  return (
    <div className="mx-auto grid max-w-6xl gap-4 lg:grid-cols-3" data-testid="day-view">
      <div className="lg:col-span-2 overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="space-y-0">
          {HOURS.map(h => {
            const cs = new Date(date.getFullYear(),date.getMonth(),date.getDate(),h);
            const ce = new Date(date.getFullYear(),date.getMonth(),date.getDate(),h+1);
            const within = dayE.filter(e => !e.all_day && overlaps(e, cs, ce));
            return (
              <div key={h} className="grid border-b border-slate-100" style={{gridTemplateColumns:"56px 1fr"}}>
                <div className="px-2 py-2 text-right text-[11px] text-slate-400">{String(h).padStart(2,"0")}:00</div>
                <div className="min-h-[48px] border-l border-slate-100 p-1 space-y-1 hover:bg-slate-50" onClick={() => canWrite && onOpenNew && onOpenNew({ start: cs })}>
                  {within.map(e => {
                    const cl = e.client_id ? clientById?.[e.client_id] : null;
                    return (
                      <button key={e.id} onClick={(ev) => { ev.stopPropagation(); onEntry(e); }}
                              className="block w-full truncate rounded px-2 py-1 text-left text-xs font-medium"
                              style={{background:hexWithAlpha(colourFor(e),0.22),color:colourFor(e)}}>
                        {new Date(e.start).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})} {e.title}
                        {e.resource_type==="vehicle" && vehicleById[e.resource_id] && ` · ${vehicleById[e.resource_id].registration}`}
                        {cl && ` · ${cl.name}`}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="day-summary">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-900">Day summary</div>
          {canWrite && onOpenNew && (
            <Button size="sm" variant="ghost" onClick={() => onOpenNew({ start: date })} data-testid="day-view-add"
                    className="gap-1 text-xs text-cargodash-primary-700 hover:bg-cargodash-primary-50">
              <Plus className="h-3.5 w-3.5" />Add entry
            </Button>
          )}
        </div>
        <div className="text-xs text-slate-500">{dayE.length} total {dayE.length===1?"entry":"entries"}</div>
        <div className="mt-4 space-y-2">
          {Object.keys(byType).length===0 && <div className="text-sm text-slate-500">No entries.</div>}
          {Object.entries(byType).map(([tid, es]) => {
            const t = typeById[tid];
            return (
              <div key={tid} className="flex items-center justify-between rounded-md border border-slate-100 px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full" style={{background: t?.colour}} />
                  <span className="text-sm">{t?.name || "Unknown"}</span>
                </div>
                <span className="font-mono text-xs text-slate-600">{es.length}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function VehiclesView({ anchor, windowDays, setWindowDays, vehicles, entries, colourFor, clientById, onEntry, onCellNew, canWrite, typeById }) {
  const days = Array.from({length:windowDays}).map((_,i) => {
    const d = new Date(anchor); d.setHours(0,0,0,0); d.setDate(d.getDate()+i); return d;
  });
  const today = new Date();
  return (
    <div className="mx-auto max-w-[1400px]" data-testid="vehicles-view">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {[7,14,30].map(n => (
            <button key={n} type="button" onClick={() => setWindowDays(n)} data-testid={`vehicles-window-${n}`}
                    className={`rounded-md px-2.5 py-1 text-xs font-medium ${windowDays===n?"bg-cargodash-primary-50 text-cargodash-primary-700":"bg-white text-slate-600 hover:bg-slate-50"} border border-slate-200`}>{n} days</button>
          ))}
          <span className="ml-2 text-[11px] text-slate-500" data-testid="vehicles-count">{vehicles.length} vehicle{vehicles.length===1?"":"s"}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-[11px]">
          {Object.values(typeById).map(t => (
            <div key={t.id} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2 py-0.5">
              <span className="h-2 w-2 rounded-full" style={{background:t.colour}} />{t.name}
            </div>
          ))}
        </div>
      </div>
      <div className="max-h-[72vh] overflow-auto rounded-xl border border-slate-200 bg-white">
        <div className="inline-block min-w-full">
          <div className="sticky top-0 z-10 grid border-b border-slate-200 bg-slate-50 text-[11px] font-medium text-slate-500 shadow-sm" style={{gridTemplateColumns:`220px repeat(${windowDays}, minmax(56px,1fr))`}}>
            <div className="border-r border-slate-200 px-3 py-2">Vehicle</div>
            {days.map(d => (
              <div key={d.toISOString()} className={`px-1 py-2 text-center ${sameDay(d,today)?"bg-cargodash-primary-50 text-cargodash-primary-700":""}`}>
                <div className="text-[9px] uppercase">{d.toLocaleDateString(undefined,{weekday:"short"})}</div>
                <div className="text-xs font-semibold text-slate-800">{d.getDate()}</div>
              </div>
            ))}
          </div>
          {vehicles.length === 0 && <div className="p-6 text-center text-sm text-slate-500">No active vehicles. Add one in Admin → Vehicles.</div>}
          {vehicles.map(v => (
            <div key={v.id} className="grid border-b border-slate-100" style={{gridTemplateColumns:`220px repeat(${windowDays}, minmax(56px,1fr))`}} data-testid={`vehicle-row-${v.id}`}>
              <div className="border-r border-slate-200 px-3 py-2">
                <div className="text-sm font-semibold text-slate-900">{v.registration}</div>
                <div className="text-[11px] text-slate-500">{v.make} {v.model}</div>
              </div>
              {days.map(d => {
                const ds = new Date(d.getFullYear(),d.getMonth(),d.getDate());
                const de = new Date(d.getFullYear(),d.getMonth(),d.getDate(),23,59,59,999);
                const booked = entries.filter(e => e.resource_type==="vehicle" && e.resource_id===v.id && overlaps(e, ds, de));
                return (
                  <div key={d.toISOString()} onClick={() => booked.length===0 && canWrite && onCellNew(v, d)}
                       data-testid={`vehicle-cell-${v.id}-${d.toISOString().slice(0,10)}`}
                       className={`relative min-h-[48px] border-l border-slate-100 p-0.5 ${booked.length===0 && canWrite ? "cursor-pointer hover:bg-slate-50" : ""}`}>
                    {booked.map(e => {
                      const cl = e.client_id ? clientById?.[e.client_id] : null;
                      const label = cl ? `${e.title} · ${cl.name}` : e.title;
                      return (
                        <button key={e.id} type="button" onClick={(ev) => { ev.stopPropagation(); onEntry(e); }}
                                title={label}
                                className="mb-0.5 block w-full truncate rounded px-1 py-0.5 text-left text-[10px] font-medium"
                                style={{background:hexWithAlpha(colourFor(e),0.3), color: colourFor(e)}}>
                          {label}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function EntryForm({ open, onOpenChange, editing, prefill, types, vehicles, users, clients, onSaved }) {
  const isEdit = !!editing;
  const [form, setForm] = useState({
    title: "", description: "", type_id: "",
    resource_type: "none", resource_id: "",
    client_id: "",
    start: "", end: "", all_day: false,
    assignee_user_id: "", colour_override: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [pendingPayload, setPendingPayload] = useState(null);

  useEffect(() => {
    if (!open) return;
    setConflicts([]); setPendingPayload(null);
    if (editing) {
      setForm({
        title: editing.title, description: editing.description || "",
        type_id: editing.type_id, resource_type: editing.resource_type || "none",
        resource_id: editing.resource_id || "",
        client_id: editing.client_id || "",
        start: fmtIsoLocal(new Date(editing.start)), end: fmtIsoLocal(new Date(editing.end)),
        all_day: !!editing.all_day,
        assignee_user_id: editing.assignee_user_id || "",
        colour_override: editing.colour_override || "",
      });
    } else {
      const now = prefill?.start ? new Date(prefill.start) : new Date();
      const start = new Date(now); start.setMinutes(0,0,0);
      const end = new Date(start); end.setHours(end.getHours()+1);
      const defaultType = types[0];
      const rt = defaultType?.resource_source === "vehicles" ? "vehicle" : defaultType?.resource_source === "users" ? "user" : "none";
      setForm({
        title: "", description: "",
        type_id: defaultType?.id || "",
        resource_type: rt,
        resource_id: prefill?.vehicle_id || "",
        client_id: prefill?.client_id || "",
        start: fmtIsoLocal(start), end: fmtIsoLocal(end),
        all_day: false, assignee_user_id: "", colour_override: "",
      });
    }
  }, [open, editing, prefill, types]);

  const selectedType = types.find(t => t.id === form.type_id);
  const showResource = selectedType && selectedType.resource_source !== "none";
  const resourceList = selectedType?.resource_source === "vehicles"
    ? vehicles.filter(v => v.status==="active").map(v => ({id:v.id,label:`${v.registration} — ${v.make} ${v.model}`}))
    : selectedType?.resource_source === "users"
      ? users.filter(u => u.status==="active").map(u => ({id:u.id,label:`${u.full_name} (${u.email})`}))
      : [];

  const buildPayload = () => {
    let s, e;
    if (form.all_day) {
      const d = new Date(form.start);
      s = new Date(d.getFullYear(),d.getMonth(),d.getDate(),0,0,0);
      e = new Date(d.getFullYear(),d.getMonth(),d.getDate(),23,59,59);
    } else {
      s = new Date(form.start); e = new Date(form.end);
    }
    return {
      title: form.title, description: form.description || null,
      type_id: form.type_id,
      resource_type: showResource ? (selectedType.resource_source === "vehicles" ? "vehicle" : "user") : "none",
      resource_id: showResource ? form.resource_id || null : null,
      client_id: form.client_id || null,
      start: s.toISOString(), end: e.toISOString(),
      all_day: form.all_day,
      assignee_user_id: form.assignee_user_id || null,
      colour_override: form.colour_override || null,
    };
  };

  const submit = async (e, ignoreConflicts=false) => {
    if (e) e.preventDefault();
    const payload = pendingPayload && ignoreConflicts ? pendingPayload : buildPayload();
    setSubmitting(true);
    try {
      const url = isEdit ? `/calendar/entries/${editing.id}` : "/calendar/entries";
      const fn = isEdit ? api.patch : api.post;
      const { data } = await fn(url, payload);
      if (!ignoreConflicts && data.conflicts && data.conflicts.length > 0) {
        setConflicts(data.conflicts);
        setPendingPayload(payload);
        // Saved anyway per warn-but-allow — but we just want to show the warning before closing
        toast.warning(`Saved with ${data.conflicts.length} conflict${data.conflicts.length===1?"":"s"}`);
      } else {
        toast.success(isEdit ? "Entry updated" : "Entry created");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err) || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-lg overflow-y-auto" data-testid="cal-entry-form">
        <SheetHeader>
          <SheetTitle>{isEdit ? "Edit entry" : "New entry"}</SheetTitle>
          <SheetDescription>All times stored as UTC; displayed in your timezone.</SheetDescription>
        </SheetHeader>
        <form onSubmit={(e) => submit(e, false)} className="mt-4 space-y-4">
          <div className="space-y-2"><Label>Title</Label>
            <Input required value={form.title} onChange={e=>setForm({...form,title:e.target.value})} data-testid="cal-form-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Type</Label>
              <Select value={form.type_id} onValueChange={v => {
                const t = types.find(x=>x.id===v);
                const rt = t?.resource_source === "vehicles" ? "vehicle" : t?.resource_source === "users" ? "user" : "none";
                setForm({...form, type_id:v, resource_type:rt, resource_id:""});
              }}>
                <SelectTrigger data-testid="cal-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>{types.map(t => <SelectItem key={t.id} value={t.id}><span className="inline-block h-2 w-2 rounded-full mr-2 align-middle" style={{background:t.colour}}/>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Colour override</Label>
              <Input type="color" value={form.colour_override || selectedType?.colour || "#16a34a"} onChange={e=>setForm({...form,colour_override:e.target.value})} className="h-10" />
            </div>
          </div>
          <div className="space-y-2"><Label>Client (optional)</Label>
            <Select value={form.client_id || "none"} onValueChange={v => setForm({...form, client_id: v === "none" ? "" : v})}>
              <SelectTrigger data-testid="cal-form-client"><SelectValue placeholder="— No client —" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— No client —</SelectItem>
                {[...(clients || [])]
                  .filter(c => c.status === "active" || c.id === form.client_id)
                  .sort((a,b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
                  .map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}{c.status === "archived" ? " (archived)" : ""}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          {showResource && (
            <div className="space-y-2"><Label>{selectedType.resource_source === "vehicles" ? "Vehicle" : "User"}</Label>
              <Select value={form.resource_id} onValueChange={v => setForm({...form, resource_id:v})}>
                <SelectTrigger data-testid="cal-form-resource"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{resourceList.map(r => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={form.all_day} onCheckedChange={v => setForm({...form,all_day:!!v})} data-testid="cal-form-all-day" />
            All-day
          </label>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Start</Label>
              <Input type={form.all_day?"date":"datetime-local"} required
                     value={form.all_day ? form.start.slice(0,10) : form.start}
                     onChange={e=>setForm({...form, start: form.all_day ? e.target.value+"T00:00" : e.target.value, end: form.all_day ? e.target.value+"T23:59" : form.end})}
                     data-testid="cal-form-start" />
            </div>
            {!form.all_day && (
              <div className="space-y-2"><Label>End</Label>
                <Input type="datetime-local" required value={form.end} onChange={e=>setForm({...form,end:e.target.value})} data-testid="cal-form-end" />
              </div>
            )}
          </div>
          {users.length > 0 && (
            <div className="space-y-2"><Label>Assignee (optional)</Label>
              <Select value={form.assignee_user_id || "none"} onValueChange={v => setForm({...form, assignee_user_id: v==="none"?"":v})}>
                <SelectTrigger data-testid="cal-form-assignee"><SelectValue placeholder="Nobody" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nobody</SelectItem>
                  {users.filter(u=>u.status==="active").map(u => <SelectItem key={u.id} value={u.id}>{u.full_name} ({u.email})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-2"><Label>Notes</Label>
            <Textarea value={form.description} onChange={e=>setForm({...form,description:e.target.value})} rows={3} />
          </div>

          {conflicts.length > 0 && (
            <div data-testid="cal-form-conflicts" className="flex items-start gap-2 rounded-md border border-cargodash-action/30 bg-orange-50 p-3 text-xs text-cargodash-action">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <div>
                <div className="font-semibold">{conflicts.length} conflict{conflicts.length===1?"":"s"} — saved anyway</div>
                <ul className="mt-1 list-disc pl-4">
                  {conflicts.slice(0,3).map(c => <li key={c.id}>{c.title} · {new Date(c.start).toLocaleString()}</li>)}
                </ul>
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={submitting} data-testid="cal-form-submit" className="flex-1 bg-cargodash-primary-600 hover:bg-cargodash-primary-700">
              {submitting ? "Saving…" : isEdit ? "Save changes" : "Create entry"}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
